// BestRx → SHWCare handoff (client). Single source of truth used by every
// handoff surface (LeadQualifierModal, MedicationZone, FinalCTA). It calls the
// SHARED backend resolver (brand=bestrx) which maps an intent to an
// owner-approved SHWCare destination with opaque referral + UTM attribution.
//
// PRIVACY: we send ONLY a coarse intent category (never name/email/phone,
// questionnaire answers, diagnosis or medication history). The backend returns
// an opaque `ref` — no PHI ever enters the URL, UTMs or analytics params.
import { track } from "../components/shared/Analytics";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// BestRx UI label / category → coarse, non-PHI intent category.
// Generic "weight loss" and "peptides" stay AMBIGUOUS on purpose so the backend
// fails safe to consultation (never guesses tirzepatide vs semaglutide).
const INTENT_MAP = {
  "weight loss": "weight_loss",
  "weight-loss": "weight_loss",
  "men's health": "mens_health",
  "mens health": "mens_health",
  "mens-health": "mens_health",
  "women's health": "womens_health",
  "womens health": "womens_health",
  "womens-health": "womens_health",
  "hair restoration": "hair",
  "hair": "hair",
  "peptides & recovery": "peptides_recovery",
  "general wellness": "wellness",
  "wellness": "wellness",
  // ── Specific medication slugs/names → approved SHWCare category ──
  // GLP-1 injectables resolve product-level; oral/generic GLP-1 stay ambiguous → consult.
  "semaglutide": "semaglutide",
  "tirzepatide": "tirzepatide",
  "oral-semaglutide": "semaglutide",
  "oral glp-1": "weight_loss",
  "oral-glp1": "weight_loss",
  // Men's-health medications → men's-health collection.
  "tadalafil": "mens_health",
  "sildenafil": "mens_health",
  "pt-141": "mens_health",
  "enclomiphene": "mens_health",
  "testosterone": "mens_health",
  "anastrozole": "mens_health",
  "dhea": "mens_health",
  // Hair-restoration medications → hair.
  "hair-restoration": "hair",
  "omg-caps": "hair",
  "brogaine": "hair",
  // Women's-health / wellness.
  "hormone-optimization": "womens_health",
  "skin-health": "womens_health",
  "mic-b12": "wellness",
  "l-carnitine": "wellness",
};

export function toIntent(label) {
  const key = String(label || "").trim().toLowerCase();
  return INTENT_MAP[key] || key;
}

// Resolve an intent to an attributed SHWCare handoff URL. Returns the resolver
// payload ({ url, routed_category, status, requires_review, ... }).
export async function resolveHandoff(intent, { campaign } = {}) {
  const params = new URLSearchParams();
  if (intent) params.set("intent", toIntent(intent));
  if (campaign) params.set("campaign", campaign);
  const r = await fetch(`${API}/handoff/resolve?${params.toString()}`);
  if (!r.ok) throw new Error(`handoff resolve failed: ${r.status}`);
  return r.json();
}

// Resolve + navigate. Fires a non-PHI GA4 handoff event, then opens SHWCare.
export async function goToShwcare(intent, { campaign, newTab = false } = {}) {
  try {
    const res = await resolveHandoff(intent, { campaign });
    track("shwcare_handoff", {
      brand: "bestrx",
      routed_category: res.routed_category,
      requires_review: res.requires_review,
    });
    if (newTab) window.open(res.url, "_blank", "noopener,noreferrer");
    else window.location.assign(res.url);
    return res;
  } catch (e) {
    // Fail safe: never break the app; fall back to the approved home page.
    window.location.assign("https://www.shwcare.com/?utm_source=bestrx&utm_medium=referral");
    return null;
  }
}
